import { LoginByEmailSuccessOutputDto } from '@starsoft/common/dto';
import { CookiesKey } from '@starsoft/common/enums';
import { QueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { deleteCookie, getCookie, hasCookie, setCookie } from 'cookies-next';
import { SettingsService } from '../SettingsService';
export class AuthService {
  static isAuthorized(): boolean {
    return (hasCookie(CookiesKey.Token) &&
      hasCookie(CookiesKey.RefreshToken)) as boolean;
  }

  static isPartiallyAuthorized(): boolean {
    return (hasCookie(CookiesKey.Token) &&
      hasCookie(CookiesKey.RefreshToken)) as boolean;
  }

  static setAsAuthorized(data?: LoginByEmailSuccessOutputDto): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (data) {
      AuthService.setToken(data.accessToken, CookiesKey.Token);
      AuthService.setRefreshToken(data.refreshToken, CookiesKey.RefreshToken);
    }
  }

  static setAsUnauthorized(isAdmin?: boolean): void {
    if (isAdmin) {
      deleteCookie(CookiesKey.TokenAdmin);
      deleteCookie(CookiesKey.RefreshTokenAdmin);
    } else {
      deleteCookie(CookiesKey.Token);
      deleteCookie(CookiesKey.RefreshToken);
    }
  }

  static setToken(token: string, key: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    setCookie(key, token, {
      maxAge: Number(SettingsService.env.JWT_EXPIRE_SECONDS as string),
      secure: true,
      // sameSite: 'strict',
    });
  }
  static setRefreshToken(token: string, key: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    setCookie(key, token, {
      maxAge: Number(SettingsService.env.JWT_EXPIRE_SECONDS as string),
      // secure: true,
      // sameSite: 'strict',
    });
  }

  static setVoucher(voucher: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    setCookie(CookiesKey.Voucher, voucher, {
      maxAge: Number(SettingsService.env.JWT_EXPIRE_SECONDS as string),
      secure: true,
      sameSite: 'strict',
    });
  }

  static removeVoucher() {
    if (typeof window === 'undefined') {
      return;
    }

    deleteCookie(CookiesKey.Voucher);
  }

  static hasVoucher(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return hasCookie(CookiesKey.Voucher) as boolean;
  }

  static getVoucher(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return getCookie(CookiesKey.Voucher) as string;
  }

  static setBetbyToken(token: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    setCookie(CookiesKey.BetbyToken, token, {
      maxAge: Number(SettingsService.env.JWT_EXPIRE_SECONDS as string),
      // secure: true,
    });
  }

  static getBetbyToken(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return getCookie(CookiesKey.BetbyToken) as string;
  }

  static removeBetbyToken(): void {
    if (typeof window === 'undefined') {
      return;
    }

    deleteCookie(CookiesKey.BetbyToken);
  }

  static hasBetbyToken(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return hasCookie(CookiesKey.BetbyToken) as boolean;
  }

  static refreshAuthorization(
    authTokenCookie: string,
    refreshTokenCookie: string,
  ) {
    if (!AuthService.isAuthorized()) {
      return;
    }

    AuthService.setAsAuthorized();

    if (hasCookie(authTokenCookie)) {
      AuthService.setToken(
        getCookie(authTokenCookie) as string,
        authTokenCookie,
      );
    }

    if (hasCookie(refreshTokenCookie)) {
      AuthService.setRefreshToken(
        getCookie(refreshTokenCookie) as string,
        refreshTokenCookie,
      );
    }
  }

  static refreshHeaders(instance: AxiosInstance) {
    if (!AuthService.isAuthorized()) {
      return;
    }

    if (hasCookie(CookiesKey.Token)) {
      instance.defaults.headers['authorization'] = getCookie(
        CookiesKey.Token,
      ) as string;
    }

    if (hasCookie(CookiesKey.RefreshToken)) {
      instance.defaults.headers['x-refresh-token'] = getCookie(
        CookiesKey.RefreshToken,
      ) as string;
    }
  }

  static logOut(
    callback?: VoidFunction,
    isAdmin?: boolean,
    queryClient?: QueryClient,
  ): void {
    AuthService.setAsUnauthorized(isAdmin);
    queryClient?.invalidateQueries?.();
    queryClient?.clear?.();

    if (isAdmin) {
      deleteCookie(CookiesKey.TokenAdmin);
      deleteCookie(CookiesKey.RefreshTokenAdmin);
    } else {
      deleteCookie(CookiesKey.HideBonusCardMobile);
      deleteCookie(CookiesKey.LastUserBonusId);
      deleteCookie(CookiesKey.BetbyToken);
      deleteCookie(CookiesKey.Voucher);
      deleteCookie(CookiesKey.Token);
      deleteCookie(CookiesKey.RefreshToken);
      deleteCookie(CookiesKey.OriginId);
    }

    if (typeof window != 'undefined' && typeof callback != 'undefined') {
      callback();
    }
  }

  static setEmailCookie(email: string) {
    setCookie(CookiesKey.UserEmail, email, {
      maxAge: 90 * 24 * 60 * 60 * 1000, //90 days,
      sameSite: 'strict',
    });
  }
}
